import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Flex, Text, Heading, Button, Badge, Spinner, VStack, HStack,
  Textarea, Icon, Grid, GridItem, Tag, Link,
} from '@chakra-ui/react';
import { BsArrowLeft, BsClock, BsCheckCircle, BsBuilding, BsPaperclip, BsFileText, BsPersonCircle } from 'react-icons/bs';
import axios from 'axios';
import Header from '../../../components/Header';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../../utils/DefaultFunction';

const API_URL = `${process.env.REACT_APP_API_URL}/customer-support`;

const customer = JSON.parse(localStorage.getItem("customer"));
  const getHeaders = () => ({
  'ngrok-skip-browser-warning': 'true',
  
   "x-api-key": localStorage.getItem("api-key"),
    "x-access-token": localStorage.getItem("customer-portal"),
});

const statusColorScheme = {
  Open: 'blue',
  'In Progress': 'orange',
  Resolved: 'green',
  Closed: 'gray',
};

const priorityColorScheme = {
  Critical: 'red',
  High: 'orange',
  Medium: 'yellow',
  Low: 'gray',
};

const priorityBg = {
  Critical: 'red.50',
  High: 'orange.50',
  Medium: 'yellow.50',
  Low: 'gray.50',
};

function initials(name = '') {
  return name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
}

function fmtDateTime(str) {
  if (!str) return '-';
  return new Date(str).toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function fmtFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeRemaining(deadline) {
  if (!deadline) return null;
  const diff = new Date(deadline) - Date.now();
  if (diff <= 0) return { label: 'Overdue', overdue: true };
  const totalMins = Math.floor(diff / 60000);
  if (totalMins < 60) return { label: `${totalMins} mins remaining`, overdue: false };
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return { label: mins > 0 ? `${hrs}h ${mins}m remaining` : `${hrs} hours remaining`, overdue: false };
}

function timeDiff(from, to) {
  if (!from || !to) return null;
  const diff = new Date(to) - new Date(from);
  if (diff <= 0) return null;
  const totalMins = Math.floor(diff / 60000);
  if (totalMins < 60) return `${totalMins} mins`;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

const sectionAccents = {
  blue:   { bg: 'blue.50',   border: 'blue.100',   icon: 'blue.500',   text: 'blue.800'   },
  indigo: { bg: 'purple.50', border: 'purple.100', icon: 'purple.500', text: 'purple.800' },
  teal:   { bg: 'teal.50',   border: 'teal.100',   icon: 'teal.500',   text: 'teal.800'   },
};

function SidebarSection({ title, icon, children, accent }) {
  const a = sectionAccents[accent];
  return (
    <Box bg="white" borderRadius="xl" borderWidth="1px" overflow="hidden" borderColor={a ? a.border : 'gray.200'}>
      <Flex
        px={4} py={3} borderBottomWidth="1px"
        bg={a ? a.bg : 'gray.50'}
        borderColor={a ? a.border : 'gray.200'}
        alignItems="center" gap={2}
      >
        {icon && <Icon as={icon} color={a ? a.icon : 'gray.500'} boxSize={3.5} />}
        <Text fontSize="xs" fontWeight="bold" color={a ? a.text : 'gray.700'}>{title}</Text>
      </Flex>
      <Box p={4}>
        {children}
      </Box>
    </Box>
  );
}

function InfoRow({ label, children }) {
  return (
    <Box>
      <Text fontSize="2xs" color="gray.400" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" mb={0.5}>{label}</Text>
      {children}
    </Box>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  const location = useLocation();
  const [featurePermissions, setFeaturePermissions] = useState([]);
  const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(true);
  // Backend groups ticket permissions under the ticket list's own feature slug,
  // not this detail page's route (which includes the ticket id).
  const permParams = "support/tickets";

  const fetchPermissions = async () => {
    setFeaturePermissionsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
      const response = await API.get(url, { headers: getApiHeaders() });
      setFeaturePermissions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setFeaturePermissions([]);
    } finally {
      setFeaturePermissionsLoading(false);
    }
  };
  useEffect(() => {
    fetchPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replying is a distinct mutating action from just viewing the ticket — the
  // ticket content itself always renders once loaded; only the reply box is
  // hidden when the user lacks permission to reply.
  const ticketReplyPermission = hasPermission("ticket-reply", featurePermissions);
  // Independent, additional gate: blocks the entire ticket detail view.
  const ticketViewPermission = hasPermission("ticket-view", featurePermissions);

  const loadTicket = useCallback(async () => {
    setLoading(true);
    try {
      const [ticketRes, messagesRes] = await Promise.all([
        axios.get(`${API_URL}/ticket-detail/${id}`, { headers: getHeaders() }),
        axios.get(`${API_URL}/ticket-messages/${id}`, { headers: getHeaders() }),
      ]);
      setTicket(ticketRes?.data?.data || ticketRes?.data);
      const msgs = messagesRes?.data?.data || messagesRes?.data || [];
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch {
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (featurePermissionsLoading || !ticketViewPermission) return;
    loadTicket();
  }, [loadTicket, featurePermissionsLoading, ticketViewPermission]);

  const handleSendReply = async () => {
    if (!reply.trim() && attachments.length === 0) return;
    const formData = new FormData();
    formData.append('message', reply);
    formData.append('message_type', 'reply');
    formData.append('sender_type', 'customer');
    attachments.forEach((file) => formData.append('attachments[]', file));
    setSending(true);
    try {
      await axios.post(`${API_URL}/ticket-message/${id}`, formData, { headers: getHeaders() });
      setReply('');
      setAttachments([]);
      loadTicket();
    } catch {
      // handle error
    } finally {
      setSending(false);
    }
  };

  const handleAttachFiles = (e) => {
    setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
    e.target.value = '';
  };

  const removeAttachment = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  if (featurePermissionsLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Flex justifyContent="center" alignItems="center" py={24} direction="column" gap={3}>
            <Spinner size="md" color="var(--brand-primary)" />
          </Flex>
        </BoxWrapper>
      </>
    );
  }

  if (!ticketViewPermission) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Flex justifyContent="center" alignItems="center" py={24} direction="column" gap={3}>
            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
            <Text fontSize="sm" color="gray.400">You don't have permission to view this ticket.</Text>
          </Flex>
        </BoxWrapper>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Flex justifyContent="center" alignItems="center" py={24} direction="column" gap={3}>
            <Spinner size="md" color="var(--brand-primary)" />
            <Text fontSize="sm" color="gray.400">Loading ticket...</Text>
          </Flex>
        </BoxWrapper>
      </>
    );
  }

  if (!ticket) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Flex justifyContent="center" alignItems="center" py={24} direction="column" gap={3}>
            <Icon as={BsFileText} color="gray.300" boxSize={10} />
            <Text color="gray.400" fontSize="sm">Ticket not found.</Text>
            <Button size="sm" variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
          </Flex>
        </BoxWrapper>
      </>
    );
  }

  const statusName = ticket.status?.name || '-';
  const priorityName = ticket.priority?.name || '-';
  const assignedTo = ticket.assignedAgent?.full_name || ticket.assignedTeam?.name || 'Unassigned';
  const sla = ticket.sla || {};
  const slaLog = ticket.slaLog || {};
  const contact = ticket.contact || null;
  const client = ticket.client || {};
  const tags = ticket.tags || [];
  const ticketAttachments = ticket.attachments || [];
  const replies = messages.filter(msg => msg.message_type !== 'internal_note' && !msg.is_draft);

  let creatorName = 'Unknown';
  if (ticket.created_by_type === 'client_user' && ticket.clientUserCreator) {
    creatorName = `${ticket.clientUserCreator.name} (${client.name || 'Client'})`;
  } else if (ticket.created_by_type === 'employee' && ticket.employeeCreator) {
    creatorName = `${ticket.employeeCreator.name} (Support Agent)`;
  }

  const resolutionRemaining = timeRemaining(slaLog.resolution_deadline);
  const firstResponseTaken = timeDiff(ticket.createdAt, ticket.first_response_at);
  const slaPolicy = sla.rule_name ? `${sla.rule_name} - ${sla.first_response_time} hours` : '-';
  const targetResolution = sla.resolution_time
    ? `${sla.resolution_time} hours${priorityName !== '-' ? ` (${priorityName})` : ''}`
    : '-';

  const MessageBubble = ({ isCustomer, name, timestamp, content, fileAttachments }) => (
    <Box position="relative" pl={isCustomer ? 0 : 11}>
      <HStack gap={2.5} mb={2.5} alignItems="flex-start">
        {isCustomer && (
          <Flex
            w={8} h={8} borderRadius="full" alignItems="center" justifyContent="center"
            bg="blue.500" color="white" fontSize="xs" fontWeight="bold" flexShrink={0}
          >
            {initials(name)}
          </Flex>
        )}
        <Box flex={1}>
          <HStack gap={2} mb={1.5} flexWrap="wrap">
            <Text fontSize="xs" fontWeight="semibold" color="gray.800">{name}</Text>
            <Text fontSize="2xs" color="gray.400">{timestamp}</Text>
          </HStack>
          <Box
            bg={isCustomer ? 'blue.50' : 'gray.50'}
            borderWidth="1px"
            borderColor={isCustomer ? 'blue.100' : 'gray.200'}
            borderRadius={isCustomer ? '2px 12px 12px 12px' : '12px 12px 12px 2px'}
            px={4}
            py={3}
          >
            <Text fontSize="xs" color="gray.700" whiteSpace="pre-line" lineHeight={1.6}>{content}</Text>
            {fileAttachments?.length > 0 && (
              <VStack mt={3} gap={1.5} align="stretch">
                {fileAttachments.map((file) => (
                  <Link
                    key={file.id}
                    href={file.s3_url}
                    isExternal
                    display="flex"
                    alignItems="center"
                    gap={2}
                    borderWidth="1px"
                    borderRadius="lg"
                    px={2.5}
                    py={1.5}
                    bg="white"
                    _hover={{ bg: 'blue.50' }}
                    fontSize="xs"
                  >
                    <Icon as={BsFileText} color="blue.400" />
                    <Text flex={1} isTruncated color="gray.700">{file.file_name}</Text>
                    <Text color="gray.400" flexShrink={0}>{fmtFileSize(file.file_size)}</Text>
                  </Link>
                ))}
              </VStack>
            )}
          </Box>
        </Box>
        {!isCustomer && (
          <Flex
            w={8} h={8} borderRadius="full" alignItems="center" justifyContent="center"
            bg="gray.200" color="gray.600" fontSize="xs" fontWeight="bold" flexShrink={0}
          >
            {initials(name)}
          </Flex>
        )}
      </HStack>
    </Box>
  );

  return (
    <>
      <Header />
      <BoxWrapper>
        <Box>
          {/* Back + Header */}
          <Flex alignItems="center" gap={3} mb={4}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<BsArrowLeft />}
              onClick={() => navigate(-1)}
              color="gray.500"
              _hover={{ bg: 'gray.100', color: 'gray.700' }}
              px={2}
            >
              Back
            </Button>
          </Flex>

          <Box mb={5} pb={4} borderBottomWidth="1px">
            <Heading as="h1" size="md" color="gray.900" mb={2}>{ticket.subject}</Heading>
            <HStack gap={2} flexWrap="wrap">
              <Text fontSize="xs" color="gray.400" fontWeight="medium" bg="gray.100" px={2} py={0.5} borderRadius="md">
                {ticket.ticket_number}
              </Text>
              <Badge colorScheme={statusColorScheme[statusName] || 'gray'} borderRadius="full" fontSize="xs" px={2.5} py={0.5} textTransform="none">
                {statusName}
              </Badge>
              {priorityName !== '-' && (
                <Badge
                  colorScheme={priorityColorScheme[priorityName] || 'gray'}
                  borderRadius="full" fontSize="xs" px={2.5} py={0.5} textTransform="none"
                  bg={priorityBg[priorityName]}
                >
                  {priorityName} Priority
                </Badge>
              )}
            </HStack>
          </Box>

          {/* Two-column layout */}
          <Grid templateColumns="1fr 280px" gap={5}>
            {/* Left: Conversation + Reply */}
            <GridItem>
              <Box bg="white" borderRadius="xl" borderWidth="1px" overflow="hidden">
                <Flex px={5} py={3.5} borderBottomWidth="1px" bg="gray.50" alignItems="center" justifyContent="space-between">
                  <Text fontSize="xs" fontWeight="bold" color="gray.700">Conversation</Text>
                  <Text fontSize="2xs" color="gray.400">{1 + replies.length} message{replies.length !== 0 ? 's' : ''}</Text>
                </Flex>
                <Box p={5}>
                  <VStack gap={4} align="stretch">
                    {/* First message: ticket description */}
                    <MessageBubble
                      isCustomer={ticket.created_by_type !== 'employee'}
                      name={creatorName}
                      timestamp={fmtDateTime(ticket.createdAt)}
                      content={ticket.description}
                      fileAttachments={ticketAttachments}
                    />

                    {/* Subsequent replies */}
                    {replies.map((msg) => {
                      const sender = msg.sender_type === 'agent' ? msg.senderEmployee : msg.senderClientUser;
                      const name = sender?.name || 'Unknown';
                      const isAgent = msg.sender_type === 'agent';
                      return (
                        <MessageBubble
                          key={msg.id}
                          isCustomer={!isAgent}
                          name={isAgent ? `${name} (Support Agent)` : name}
                          timestamp={fmtDateTime(msg.sent_at)}
                          content={msg.message}
                          fileAttachments={msg.attachments}
                        />
                      );
                    })}
                  </VStack>

                  {/* Reply box */}
                  {ticketReplyPermission && (
                    <Box mt={6} pt={5} borderTopWidth="1px">
                      <Text fontSize="xs" fontWeight="bold" color="gray.700" mb={3}>Send a Reply</Text>
                      <Box borderWidth="1px" borderRadius="xl" overflow="hidden" _focusWithin={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3b82f6' }} transition="all 0.15s">
                        <Box p={4}>
                          <Textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder="Type your reply here..."
                            rows={4}
                            border="none"
                            _focus={{ boxShadow: 'none' }}
                            fontSize="sm"
                            resize="none"
                            color="gray.700"
                          />
                        </Box>

                        {attachments.length > 0 && (
                          <VStack px={4} py={2} borderTopWidth="1px" gap={1.5} align="stretch">
                            {attachments.map((file, i) => (
                              <HStack key={i} px={3} py={1.5} borderWidth="1px" borderRadius="lg" bg="gray.50" fontSize="xs">
                                <Icon as={BsFileText} color="blue.400" />
                                <Text flex={1} isTruncated color="gray.700">{file.name}</Text>
                                <Text color="gray.400">({(file.size / 1024).toFixed(0)} KB)</Text>
                                <Button size="xs" variant="ghost" color="gray.400" _hover={{ color: 'red.500' }} onClick={() => removeAttachment(i)}>
                                  ×
                                </Button>
                              </HStack>
                            ))}
                          </VStack>
                        )}

                        <Flex px={4} py={3} bg="gray.50" borderTopWidth="1px" alignItems="center" justifyContent="space-between">
                          <Button as="label" variant="ghost" size="xs" leftIcon={<BsPaperclip />} cursor="pointer" color="gray.500" _hover={{ color: 'blue.500' }}>
                            Attach files
                            <input type="file" multiple hidden onChange={handleAttachFiles} />
                          </Button>
                          <Button
                            size="sm"
                            bg="var(--brand-primary)"
                            color="white"
                            _hover={{ bg: 'var(--brand-primary-active)' }}
                            borderRadius="lg"
                            onClick={handleSendReply}
                            isLoading={sending}
                            isDisabled={!reply.trim() && attachments.length === 0}
                          >
                            Send Reply
                          </Button>
                        </Flex>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </GridItem>

            {/* Right sidebar */}
            <GridItem>
              <VStack gap={3} align="stretch">
                {/* SLA Information */}
                <SidebarSection title="SLA Information" icon={BsClock} accent="blue">
                  <VStack gap={3} align="stretch">
                    <InfoRow label="SLA Policy">
                      <Text fontSize="xs" color="blue.600" fontWeight="medium">{slaPolicy}</Text>
                    </InfoRow>
                    {priorityName !== '-' && (
                      <InfoRow label="Priority">
                        <Badge colorScheme={priorityColorScheme[priorityName] || 'gray'} borderRadius="full" fontSize="xs" textTransform="none">
                          {priorityName}
                        </Badge>
                      </InfoRow>
                    )}
                    <InfoRow label="First Response">
                      {ticket.first_response_at ? (
                        <HStack gap={1.5}>
                          <Icon as={BsCheckCircle} color="green.500" boxSize={3.5} />
                          <Text fontSize="xs" color="green.600" fontWeight="medium">
                            Met{firstResponseTaken ? ` (${firstResponseTaken})` : ''}
                          </Text>
                        </HStack>
                      ) : (
                        <HStack gap={1.5}>
                          <Icon as={BsClock} color="orange.400" boxSize={3} />
                          <Text fontSize="xs" color="orange.500" fontWeight="medium">
                            Pending{slaLog.first_response_deadline ? ` — due ${fmtDateTime(slaLog.first_response_deadline)}` : ''}
                          </Text>
                        </HStack>
                      )}
                    </InfoRow>
                    <InfoRow label="Resolution Due">
                      {slaLog.resolution_met ? (
                        <HStack gap={1.5}>
                          <Icon as={BsCheckCircle} color="green.500" boxSize={3} />
                          <Text fontSize="xs" color="green.600" fontWeight="medium">Resolved</Text>
                        </HStack>
                      ) : resolutionRemaining ? (
                        <HStack gap={1.5}>
                          <Icon as={BsClock} color={resolutionRemaining.overdue ? 'red.500' : 'orange.500'} boxSize={3} />
                          <Text fontSize="xs" fontWeight="medium" color={resolutionRemaining.overdue ? 'red.500' : 'orange.500'}>
                            {resolutionRemaining.label}
                          </Text>
                        </HStack>
                      ) : (
                        <Text fontSize="xs" color="gray.400">-</Text>
                      )}
                    </InfoRow>
                    <InfoRow label="Target Resolution">
                      <Text fontSize="xs" color="gray.700" fontWeight="medium">{targetResolution}</Text>
                    </InfoRow>
                  </VStack>
                </SidebarSection>

                {/* Ticket Properties */}
                <SidebarSection title="Ticket Properties" accent="indigo">
                  <VStack gap={3} align="stretch">
                    <InfoRow label="Status">
                      <Badge colorScheme={statusColorScheme[statusName] || 'gray'} borderRadius="full" fontSize="xs" textTransform="none">
                        {statusName}
                      </Badge>
                    </InfoRow>
                    <InfoRow label="Assigned To">
                      <HStack gap={1.5}>
                        <Icon as={BsPersonCircle} color="gray.400" boxSize={3.5} />
                        <Text fontSize="xs" fontWeight="semibold" color="gray.800">{assignedTo}</Text>
                      </HStack>
                    </InfoRow>
                    <InfoRow label="Product">
                      <Text fontSize="xs" color="gray.700">{ticket.product?.name || '-'}</Text>
                    </InfoRow>
                    <InfoRow label="Category">
                      <Text fontSize="xs" color="gray.700">{ticket.category?.name || '-'}</Text>
                    </InfoRow>
                    <InfoRow label="Sub-Category">
                      <Text fontSize="xs" color="gray.700">{ticket.subCategory?.name || '-'}</Text>
                    </InfoRow>
                    {tags.length > 0 && (
                      <InfoRow label="Tags">
                        <HStack flexWrap="wrap" gap={1.5} mt={0.5}>
                          {tags.map((tag, i) => (
                            <Tag key={i} size="sm" borderRadius="full" variant="outline" fontSize="2xs">{tag.name || tag}</Tag>
                          ))}
                        </HStack>
                      </InfoRow>
                    )}
                  </VStack>
                </SidebarSection>

                {/* Customer Information */}
                <SidebarSection title="Customer Information" icon={BsBuilding} accent="teal">
                  <VStack gap={3} align="stretch">
                    <InfoRow label="Name">
                      <Text fontSize="xs" color="gray.700">{contact?.name || client.name || '-'}</Text>
                    </InfoRow>
                    <InfoRow label="Email">
                      {(contact?.email || ticket.email) ? (
                        <Link href={`mailto:${contact?.email || ticket.email}`} fontSize="xs" color="blue.600" _hover={{ textDecoration: 'underline' }}>
                          {contact?.email || ticket.email}
                        </Link>
                      ) : (
                        <Text fontSize="xs" color="gray.400">-</Text>
                      )}
                    </InfoRow>
                    <InfoRow label="Phone">
                      <Text fontSize="xs" color="gray.700">{contact?.phone_no || '-'}</Text>
                    </InfoRow>
                    <InfoRow label="Company">
                      <HStack gap={1.5}>
                        <Icon as={BsBuilding} color="gray.400" boxSize={3} />
                        <Text fontSize="xs" color="gray.700">{client.name || '-'}</Text>
                      </HStack>
                    </InfoRow>
                  </VStack>
                </SidebarSection>
              </VStack>
            </GridItem>
          </Grid>
        </Box>
      </BoxWrapper>
    </>
  );
}
